import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import ExpandLess from '@mui/icons-material/ExpandLess'
import ExpandMore from '@mui/icons-material/ExpandMore'
import {
  Box,
  Divider,
  List,
  ListItemButton,
  ListItemText,
  Collapse,
  Paper,
  Typography,
  CircularProgress,
} from '@mui/material'
import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { DropDown } from 'Common/Supplyvue-ui'
import { MenuItem } from 'Common/Supplyvue-ui/DropDown.types'
import { useProjectData } from 'Features/Projects/Scenarios/Scenarios.data'
import { Scenario } from 'Features/Projects/Scenarios/Scenarios.types'

type Category = {
  label: string
  subcategories: string[]
}

const tempCategories: Category[] = [
  {
    label: 'Portfolio settings',
    subcategories: ['Sales Rate', 'Service Level'],
  },
  {
    label: 'Network settings',
    subcategories: ['Site stock flow', 'Replenishment', 'Network Capacities'],
  },
  {
    label: 'Factory settings',
    subcategories: [
      'Allocations',
      'Capacities',
      'Batch Production',
      'SKU Production',
      'Changeovers',
    ],
  },
  {
    label: 'Summary',
    subcategories: ['Template summary', 'Inventory summary'],
  },
]

const defaultOpenCategories = tempCategories.reduce(
  (acc, category) => ({
    ...acc,
    [category.label]: true,
  }),
  {}
)

export const DrawerComponent = ({ open }: { open: boolean }) => {
  const { projectId, scenarioId } = useParams<{
    projectId: string
    scenarioId: string
  }>()

  const { data: projectData } = useProjectData(projectId)
  const [selectedScenario, setSelectedScenario] = useState<string | undefined>(
    undefined
  )
  const [scenarios, setScenarios] = useState<Scenario[]>([])

  useEffect(() => {
    if (projectData) {
      const scenario = projectData.scenariosList.find(
        (scenario) => scenario.scenarioId === scenarioId
      )?.scenarioName
      setSelectedScenario(scenario)
      setScenarios(
        projectData.scenariosList.filter((scenario) => scenario.scenarioName)
      )
    }
  }, [projectData])

  const [openCategories, setOpenCategories] = useState<Record<string, boolean>>(
    defaultOpenCategories
  )

  const [categoryIndex, setCategoryIndex] = useState<number | null>(null)

  const navigate = useNavigate()
  const handleSubCategoryClick = (
    subCategory: string,
    categoryIndex: number,
    subCategoryIndex: number
  ) => {
    setCategoryIndex(categoryIndex * 10 + subCategoryIndex)
    navigate(subCategory?.toLocaleLowerCase().replace(/\s/g, '-'))
  }

  const handleCategoryClick = (label: string) => {
    setOpenCategories((prev) => ({
      ...prev,
      [label]: !prev[label],
    }))
  }

  const dropdownOptions: MenuItem[] = scenarios.map((scenario: Scenario) => {
    return { label: scenario.scenarioName, value: scenario.scenarioName }
  })

  const handleOnChange = (e: any) => {
    const scenarioName = e.target.value
    const scenarioId = scenarios.find(
      (scenario: Scenario) => scenario.scenarioName === scenarioName
    )?.scenarioId
    if (scenarioId) {
      navigate(
        `/projects/${projectId}/scenarios/${scenarioId}/scenario/template-summary`
      )
    }
    setSelectedScenario(scenarioName)
  }

  return (
    <Box
      display="flex"
      sx={{
        width: '260px',
        flex: 1,
        minHeight: '1200px',
        borderLeft: '2px solid lightgrey',
        boxShadow: '4px 0 6px -2px rgba(197,197,197,0.5)',
      }}
    >
      <Paper
        elevation={0}
        sx={{ width: '100%', borderRadius: '0px', height: '100%' }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            padding: '16px 20px',
            gap: '16px',
          }}
        >
          <Typography variant="h6" fontWeight="bold">
            Scenario
          </Typography>
          {!selectedScenario ? (
            <Box
              display="flex"
              flexDirection="row"
              justifyContent="space-between"
              alignItems="center"
              sx={(theme) => ({
                borderRadius: 0,
                border: `1px solid ${theme.palette.grey[400]}`,
                paddingLeft: '12px',
                paddingRight: '8px',
                width: '100%',
                height: 40,
              })}
            >
              <Box display="flex">
                <CircularProgress
                  size={16}
                  sx={(theme) => ({
                    color: theme.palette.grey[400],
                  })}
                />
              </Box>
              <Box display="flex">
                <ArrowDropDownIcon
                  sx={(theme) => ({
                    color: theme.palette.grey[400],
                  })}
                />
              </Box>
            </Box>
          ) : (
            <DropDown
              value={selectedScenario}
              name={'ScenarioSelect'}
              menuItems={dropdownOptions}
              label={'ScenarioSelect'}
              defaultLabel={'Select Scenario'}
              onChange={handleOnChange}
              variant={'outlined'}
              formControlSx={{
                minHeight: '40px',
                height: 'auto',
              }}
              sx={{
                height: '40px',
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: 'rgba(0, 0, 0, 0.23) !important',
                    borderWidth: '1px !important',
                    borderStyle: 'solid !important',
                  },
                  '&:hover fieldset': {
                    borderColor: 'rgba(0, 0, 0, 0.87) !important',
                    borderWidth: '1px !important',
                    borderStyle: 'solid !important',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#1976d2 !important',
                    borderWidth: '2px !important',
                    borderStyle: 'solid !important',
                  },
                },
              }}
            />
          )}
        </Box>
        <Divider
          variant="fullWidth"
          sx={{ color: 'lightgrey' }}
          component="div"
        />
        <List>
          {tempCategories.map((category, index) => (
            <Box
              key={category.label}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                paddingTop: '2px',
              }}
            >
              <ListItemButton
                onClick={() => handleCategoryClick(category.label)}
                key={category.label}
              >
                {open &&
                  (openCategories[category.label] ? (
                    <ExpandLess />
                  ) : (
                    <ExpandMore />
                  ))}
                <ListItemText
                  sx={{ paddingLeft: '4px' }}
                  primary={open ? category.label : ''}
                />
              </ListItemButton>
              <Collapse
                in={openCategories[category.label]}
                timeout="auto"
                unmountOnExit
              >
                <List component="div" disablePadding>
                  {category.subcategories.map((sub, subIndex) => (
                    <Box
                      key={`${sub}-${subIndex}`}
                      sx={{
                        display: 'flex',
                        padding: '0px 16px',
                      }}
                    >
                      <ListItemButton
                        sx={{
                          padding: '0px 16px',
                          borderRadius: '8px',
                          height: '44px',
                        }}
                        selected={categoryIndex === index * 10 + subIndex}
                        onClick={() =>
                          handleSubCategoryClick(sub, index, subIndex)
                        }
                      >
                        <ListItemText
                          sx={{ paddingLeft: '36px' }}
                          primary={open ? sub : ''}
                        />
                      </ListItemButton>
                    </Box>
                  ))}
                  <Divider
                    sx={{ marginTop: '8px' }}
                    variant="fullWidth"
                    component="div"
                  />
                </List>
              </Collapse>
            </Box>
          ))}
        </List>
      </Paper>
    </Box>
  )
}
